// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import * as THREE from 'three';
import { MeshBVH } from 'three-mesh-bvh';

import type { Vec3 } from '../bygning/ruler';
import { triangleFeatures, type Ray, type SnapFeature } from '../bygning/snapping';
import type { PointCloudScene } from './PointCloudScene';

/**
 * What lies under the pointer for the measure tools' snapping (frontend-aa2),
 * in world coordinates: the pick ray, the scan's nearest cloud point within
 * the radius, and the mesh surface hit with its triangle's corners and edge
 * midpoints. The BIM features and the ranking are `bygning/snapping.ts`.
 */
export interface SurfaceProbe {
  ray: Ray;
  /** The front-most cloud point within the radius, nearest the pointer. */
  cloud: { world: Vec3; px: number; depth: number } | null;
  /** The first mesh surface under the pointer. */
  mesh: { world: Vec3; depth: number; features: SnapFeature[] } | null;
}

/** Cloud points this much (or 2 %) behind the front-most one still count as the front surface, metres. */
const FRONT_SLACK = 0.15;

/** Whether an object and every parent are visible. */
function shown(object: THREE.Object3D): boolean {
  for (let o: THREE.Object3D | null = object; o; o = o.parent) if (!o.visible) return false;
  return true;
}

/** Points per chunk of the cloud search's culling. */
const CHUNK = 128;

/**
 * A page's points in space-filling (Morton) order, cut into chunks with
 * their bounding spheres (x, y, z, r; stride 4): whatever order the points
 * arrive in, a chunk then holds neighbours, so a probe skips all but the
 * few near the pointer. Built once per page (the positions never change).
 */
interface Chunks {
  perm: Uint32Array;
  spheres: Float32Array;
}
const chunkCache = new WeakMap<THREE.BufferAttribute, Chunks>();
function chunksOf(pos: THREE.BufferAttribute): Chunks {
  const cached = chunkCache.get(pos);
  if (cached) return cached;
  const a = pos.array as ArrayLike<number>;
  const n = pos.count;
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < n; i += 1) {
    const x = a[i * 3];
    const y = a[i * 3 + 1];
    const z = a[i * 3 + 2];
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  const span = Math.max(maxX - minX, maxY - minY, maxZ - minZ, 1e-9);
  const q = (v: number, min: number) => Math.min(1023, Math.max(0, Math.floor(((v - min) / span) * 1024)));
  const spread = (v: number) => {
    let x = v & 0x3ff;
    x = (x | (x << 16)) & 0x30000ff;
    x = (x | (x << 8)) & 0x300f00f;
    x = (x | (x << 4)) & 0x30c30c3;
    x = (x | (x << 2)) & 0x9249249;
    return x;
  };
  // Key and index packed in one float64 (30 + 22 bits): one native numeric sort.
  const big = n < 1 << 22;
  const keys = new Float64Array(n);
  for (let i = 0; i < n; i += 1) {
    const m = (spread(q(a[i * 3], minX)) | (spread(q(a[i * 3 + 1], minY)) << 1) | (spread(q(a[i * 3 + 2], minZ)) << 2)) >>> 0;
    keys[i] = big ? m * 4194304 + i : i;
  }
  keys.sort();
  const perm = new Uint32Array(n);
  for (let k = 0; k < n; k += 1) perm[k] = big ? keys[k] % 4194304 : keys[k];
  const chunks = Math.ceil(n / CHUNK);
  const spheres = new Float32Array(chunks * 4);
  for (let c = 0; c < chunks; c += 1) {
    let x0 = Infinity;
    let y0 = Infinity;
    let z0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    let z1 = -Infinity;
    for (let k = c * CHUNK, last = Math.min(n, k + CHUNK); k < last; k += 1) {
      const i = perm[k] * 3;
      const x = a[i];
      const y = a[i + 1];
      const z = a[i + 2];
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
      if (z < z0) z0 = z;
      if (z > z1) z1 = z;
    }
    spheres[c * 4] = (x0 + x1) / 2;
    spheres[c * 4 + 1] = (y0 + y1) / 2;
    spheres[c * 4 + 2] = (z0 + z1) / 2;
    spheres[c * 4 + 3] = Math.hypot(x1 - x0, y1 - y0, z1 - z0) / 2;
  }
  const out = { perm, spheres };
  chunkCache.set(pos, out);
  return out;
}

/** Where each point sits in the draw order (the inverse of the index), rebuilt when the order changes. */
const rankCache = new WeakMap<THREE.BufferAttribute, { version: number; rank: Uint32Array }>();
function rankOf(order: THREE.BufferAttribute): Uint32Array {
  const cached = rankCache.get(order);
  if (cached && cached.version === order.version) return cached.rank;
  const idx = order.array as ArrayLike<number>;
  const rank = new Uint32Array(order.count);
  for (let k = 0; k < order.count; k += 1) rank[idx[k]] = k;
  rankCache.set(order, { version: order.version, rank });
  return rank;
}

/**
 * The cloud passes grouped by page (the solid and ghost passes share one
 * position attribute and one index): which index positions are drawn —
 * the solid pass's draw range, and the ghost pass's where it shows.
 */
function drawnPages(objects: readonly THREE.Points[]): {
  object: THREE.Points;
  pos: THREE.BufferAttribute;
  order: THREE.BufferAttribute;
  drawn: (k: number) => boolean;
}[] {
  const byPos = new Map<THREE.BufferAttribute, { object: THREE.Points; order: THREE.BufferAttribute; ranges: [number, number][] }>();
  for (const object of objects) {
    const geometry = object.geometry;
    const pos = geometry.getAttribute('position');
    const order = geometry.getIndex();
    if (!(pos instanceof THREE.BufferAttribute) || !order) continue;
    const start = geometry.drawRange.start;
    const end = Math.min(order.count, start + (Number.isFinite(geometry.drawRange.count) ? geometry.drawRange.count : order.count));
    const entry = byPos.get(pos) ?? { object, order, ranges: [] };
    if (end > start) entry.ranges.push([start, end]);
    byPos.set(pos, entry);
  }
  return [...byPos].map(([pos, { object, order, ranges }]) => ({
    object,
    pos,
    order,
    drawn: (k: number) => {
      for (const [s, e] of ranges) if (k >= s && k < e) return true;
      return false;
    },
  }));
}

/** A BVH per mesh geometry, built on the first probe that meets it (three-mesh-bvh, MIT). */
const bvhs = new WeakMap<THREE.BufferGeometry, MeshBVH>();
function bvhOf(geometry: THREE.BufferGeometry): MeshBVH {
  let bvh = bvhs.get(geometry);
  if (!bvh) {
    // Indirect: the geometry (shared with the section caps, and cached) is left as it is.
    bvh = new MeshBVH(geometry, { indirect: true });
    bvhs.set(geometry, bvh);
  }
  return bvh;
}

/** The snap meshes in the scene, re-collected only when the scene's top-level children change. */
const targetCache = new WeakMap<THREE.Scene, { key: string; meshes: THREE.Mesh[] }>();
function snapMeshes(root: THREE.Scene): THREE.Mesh[] {
  const key = root.children.map((c) => `${c.id}:${c.children.length}`).join(',');
  const cached = targetCache.get(root);
  if (cached && cached.key === key) return cached.meshes;
  const meshes: THREE.Mesh[] = [];
  root.traverse((obj) => {
    if ((obj as THREE.Mesh).isMesh && obj.userData.snapTarget === true) meshes.push(obj as THREE.Mesh);
  });
  targetCache.set(root, { key, meshes });
  return meshes;
}

/**
 * Probe the scene under canvas pixel (x, y) for snap targets within
 * `radiusPx`. Honours the clipping box and the plan's section (a point cut
 * away is not on screen) and hidden layers. Null before any content.
 *
 * The cloud is searched in screen space, so the radius is exact in pixels
 * at any depth and in either projection: each page is cut once into
 * 128-point chunks of Morton-sorted neighbours (`chunksOf`), only the chunks
 * whose bounding sphere comes within reach are projected, and nothing is
 * allocated per point (a 1M-point mock: ~90k points projected, 8–10 ms a
 * probe in headless software rendering). Meshes are ray cast through a
 * per-geometry BVH (three-mesh-bvh).
 */
export function probeSurface(scene: PointCloudScene, x: number, y: number, radiusPx: number): SurfaceProbe | null {
  const origin = scene.worldOrigin();
  const canvas = scene.getCanvas();
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  if (!origin || width === 0 || height === 0) return null;
  const o = new THREE.Vector3(origin[0], origin[1], origin[2]);
  const ndc = new THREE.Vector2((x / width) * 2 - 1, -(y / height) * 2 + 1);
  const camera = scene.getCamera();
  camera.updateMatrixWorld();
  const raycaster = scene.raycasterAt(ndc);
  const rayO = raycaster.ray.origin;
  const rayD = raycaster.ray.direction;
  const ray: Ray = {
    origin: [rayO.x + o.x, rayO.y + o.y, rayO.z + o.z],
    dir: [rayD.x, rayD.y, rayD.z],
  };
  const planes = scene.activeClippingPlanes();
  const kept = (p: THREE.Vector3) => planes.every((pl) => pl.distanceToPoint(p) >= 0);
  const depthOf = (p: THREE.Vector3) => (p.x - rayO.x) * rayD.x + (p.y - rayO.y) * rayD.y + (p.z - rayO.z) * rayD.z;
  const toWorld = (p: THREE.Vector3): Vec3 => [p.x + o.x, p.y + o.y, p.z + o.z];

  // --- the cloud: screen space --------------------------------------------------

  const viewProj = new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
  const halfW = width / 2;
  const halfH = height / 2;
  const r2 = radiusPx * radiusPx;
  const found: { pos: THREE.BufferAttribute; i: number; m: THREE.Matrix4; px: number }[] = [];
  const mvp = new THREE.Matrix4();
  const view = new THREE.Matrix4();
  const perspective = camera instanceof THREE.PerspectiveCamera;
  const focalPx = camera instanceof THREE.PerspectiveCamera ? halfH / Math.tan((camera.fov * Math.PI) / 360) : 0;
  const orthoPxPerM = camera instanceof THREE.OrthographicCamera ? (height * camera.zoom) / (camera.top - camera.bottom) : 0;
  for (const page of drawnPages(scene.snapClouds())) {
    const { pos, object } = page;
    const { perm, spheres: chunks } = chunksOf(pos);
    const rank = rankOf(page.order);
    mvp.multiplyMatrices(viewProj, object.matrixWorld);
    view.multiplyMatrices(camera.matrixWorldInverse, object.matrixWorld);
    const e = mvp.elements;
    const v = view.elements;
    const arr = pos.array as ArrayLike<number>;
    const count = pos.count;
    for (let c = 0; c * CHUNK < count; c += 1) {
      // A chunk (consecutive points: the cloud is stored in Morton order, so
      // they lie together) whose bounding sphere stays out of reach is skipped.
      const cx = chunks[c * 4];
      const cy = chunks[c * 4 + 1];
      const cz = chunks[c * 4 + 2];
      const cr = chunks[c * 4 + 3];
      const cw = e[3] * cx + e[7] * cy + e[11] * cz + e[15];
      const depth = -(v[2] * cx + v[6] * cy + v[10] * cz + v[14]);
      if (perspective ? depth > cr && cw > 0 : true) {
        const sx = ((e[0] * cx + e[4] * cy + e[8] * cz + e[12]) / cw + 1) * halfW - x;
        const sy = (1 - (e[1] * cx + e[5] * cy + e[9] * cz + e[13]) / cw) * halfH - y;
        const reach = radiusPx + (perspective ? (cr * focalPx) / (depth - cr) : cr * orthoPxPerM);
        if (sx * sx + sy * sy > reach * reach) continue;
      } else if (perspective && depth + cr < 0) continue; // wholly behind the camera
      const last = Math.min(count, (c + 1) * CHUNK);
      for (let k = c * CHUNK; k < last; k += 1) {
        const i = perm[k];
        const b = i * 3;
        const px = arr[b];
        const py = arr[b + 1];
        const pz = arr[b + 2];
        const w = e[3] * px + e[7] * py + e[11] * pz + e[15];
        if (!(w > 0)) continue;
        const sx = ((e[0] * px + e[4] * py + e[8] * pz + e[12]) / w + 1) * halfW - x;
        if (sx > radiusPx || sx < -radiusPx) continue;
        const sy = (1 - (e[1] * px + e[5] * py + e[9] * pz + e[13]) / w) * halfH - y;
        const d2 = sx * sx + sy * sy;
        if (d2 > r2) continue;
        const sz = (e[2] * px + e[6] * py + e[10] * pz + e[14]) / w;
        if (sz < -1 || sz > 1) continue;
        // Hidden points (outside the draw ranges) are rare near the pointer: checked last.
        if (!page.drawn(rank[i])) continue;
        found.push({ pos, i, m: object.matrixWorld, px: Math.sqrt(d2) });
      }
    }
  }
  let cloud: SurfaceProbe['cloud'] = null;
  if (found.length > 0) {
    const hits: { p: THREE.Vector3; px: number; depth: number }[] = [];
    let front = Infinity;
    for (const f of found) {
      const p = new THREE.Vector3().fromBufferAttribute(f.pos, f.i).applyMatrix4(f.m);
      if (!kept(p)) continue;
      const depth = depthOf(p);
      if (depth < front) front = depth;
      hits.push({ p, px: f.px, depth });
    }
    const slack = Math.max(FRONT_SLACK, 0.02 * Math.abs(front));
    let best: (typeof hits)[number] | null = null;
    for (const h of hits) {
      if (h.depth > front + slack) continue;
      if (!best || h.px < best.px) best = h;
    }
    if (best) cloud = { world: toWorld(best.p), px: best.px, depth: best.depth };
  }

  // --- meshes: the first surface hit (BVH), and its triangle ---------------------

  let mesh: SurfaceProbe['mesh'] = null;
  const inverse = new THREE.Matrix4();
  const local = new THREE.Ray();
  let nearest = Infinity;
  for (const object of snapMeshes(scene.sceneRoot())) {
    if (!shown(object)) continue;
    object.updateMatrixWorld();
    inverse.copy(object.matrixWorld).invert();
    local.copy(raycaster.ray).applyMatrix4(inverse);
    for (const hit of bvhOf(object.geometry).raycast(local, THREE.DoubleSide)) {
      if (!hit.face) continue;
      const p = hit.point.clone().applyMatrix4(object.matrixWorld);
      if (!kept(p)) continue;
      const depth = depthOf(p);
      if (depth >= nearest) continue;
      nearest = depth;
      const posAttr = object.geometry.getAttribute('position');
      const corner = (i: number): Vec3 => toWorld(new THREE.Vector3().fromBufferAttribute(posAttr, i).applyMatrix4(object.matrixWorld));
      mesh = {
        world: toWorld(p),
        depth,
        features: triangleFeatures(corner(hit.face.a), corner(hit.face.b), corner(hit.face.c)),
      };
    }
  }

  return { ray, cloud, mesh };
}

/** Build the meshes' BVHs and the clouds' chunks ahead of the first probe (a tool switched on). */
export function prepareSnapMeshes(scene: PointCloudScene): void {
  for (const object of snapMeshes(scene.sceneRoot())) bvhOf(object.geometry);
  for (const page of drawnPages(scene.snapClouds())) chunksOf(page.pos);
}
